import { createNoise2D } from "simplex-noise";
import { LikeConfig } from "./config";
import { useMemo } from "react";
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { QueryKeysConfig } from "@/config/query-keys";
import { useLikePostRequest } from "@/hooks/network/use-like-post-request";
import { cloneDeep } from "@/utils/clone-deep";
import { getLikedPosts } from "@/persistence/liked-posts";
import type { TPost } from "@/network/typings";
const simplex = createNoise2D();

export const useParticleX = (idx: number) => {
  return useMemo(() => {
    return simplex(idx, 1) * ((LikeConfig.heartSize - 15) / 2);
  }, [idx]);
};

export const useHasLike = (postId: number) => {
  const { data: hasLike } = useSuspenseQuery({
    queryKey: [QueryKeysConfig.likedPosts],
    queryFn: getLikedPosts,
    select: (posts) => posts.includes(postId),
  });
  return hasLike;
};

export const useLikePost = (postId: number) => {
  const queryClient = useQueryClient();
  const makeRequest = useLikePostRequest();
  const mutation = useMutation({
    onSuccess() {
      for (const queryKey of [
        [QueryKeysConfig.rootPosts],
        [QueryKeysConfig.postsByTag],
        [QueryKeysConfig.searchPostsByTerm],
      ]) {
        queryClient.invalidateQueries({ queryKey });
      }
    },
    onMutate: () => {
      queryClient.setQueryData<number[]>(
        [QueryKeysConfig.likedPosts],
        (liked) => {
          if (!liked) return liked;
          return liked.concat(postId);
        },
      );
      queryClient.setQueriesData<TPost>(
        { queryKey: [QueryKeysConfig.postBySlug] },
        (data) => {
          if (data?.id === postId) {
            const cloned = cloneDeep(data);
            cloned.likesCount += 1;
            return cloned;
          }
        },
      );
    },
    onError(e) {
      console.error(e);
      queryClient.invalidateQueries({ queryKey: [QueryKeysConfig.likedPosts] });
      queryClient.invalidateQueries({ queryKey: [QueryKeysConfig.postBySlug] });
    },
    mutationFn: () => makeRequest({ data: { postId } }),
  });
  return () => mutation.mutate();
};
